import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  Clock,
  TrendingUp,
  XCircle,
} from "lucide-react";
import { useWorkoutHistory } from "@/lib/queries";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { EvolutionCharts } from "@/components/evolucao/EvolutionCharts";
import {
  WindowFilter,
  windowDays,
  type WindowKey,
} from "@/components/evolucao/WindowFilter";
import { cn } from "@/lib/cn";
import type { StudentExecution } from "@/lib/types";

/**
 * Evolução do aluno — portada de `frontend/components/portal/EvolucaoView.tsx`.
 * Busca 90 dias uma vez (useWorkoutHistory) e filtra client-side por janela.
 * Mostra gráficos (aderência semanal + progressão de carga) e a timeline das
 * sessões mais recentes, com link pro histórico completo.
 */

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  } catch {
    return "—";
  }
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function formatDuration(min: number | null | undefined): string | null {
  if (min == null || !Number.isFinite(min)) return null;
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

function statusVisual(status: StudentExecution["status"]) {
  if (status === "completed") return { Icon: CheckCircle2, color: "text-accent-soft" };
  if (status === "abandoned") return { Icon: XCircle, color: "text-danger" };
  return { Icon: CircleDashed, color: "text-yellow-400/90" };
}

export function EvolucaoView() {
  const { slug = "" } = useParams();
  const [filter, setFilter] = useState<WindowKey>("30d");
  const history = useWorkoutHistory(90);

  const executions = history.data?.executions ?? [];

  const filtered = useMemo(() => {
    const cutoff = Date.now() - windowDays(filter) * 24 * 60 * 60 * 1000;
    return executions.filter((e) => Date.parse(e.executed_at) >= cutoff);
  }, [executions, filter]);

  return (
    <div className="animate-fade-in px-5 pt-8">
      <header className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
          Sua jornada
        </p>
        <h1 className="font-display text-xl text-ink">Evolução</h1>
      </header>

      {history.isLoading ? (
        <Spinner fullscreen />
      ) : (
        <div className="space-y-4">
          <WindowFilter value={filter} onChange={setFilter} />

          <EvolutionCharts executions={filtered} />

          <section>
            <div className="mb-2.5 flex items-end justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
                  Histórico recente
                </p>
                <h2 className="font-display text-base text-ink">Últimas sessões</h2>
              </div>
              <Link
                to={`/s/${slug}/historico`}
                className="inline-flex items-center gap-1 text-xs font-medium text-accent-soft transition-colors hover:text-ink"
              >
                Ver tudo
                <ChevronRight size={12} aria-hidden />
              </Link>
            </div>

            {filtered.length === 0 ? (
              <EmptyState
                icon={TrendingUp}
                title="Sem sessões neste período"
                description="Quando você registrar um treino, ele aparece aqui."
              />
            ) : (
              <ul className="space-y-2">
                {filtered.slice(0, 8).map((exec) => {
                  const completedItems = exec.items.filter((i) => !!i.completed_at).length;
                  const { Icon, color } = statusVisual(exec.status);
                  const duration = formatDuration(exec.duration_minutes);
                  return (
                    <li key={exec.id}>
                      <div className="flex items-center gap-3 rounded-2xl border border-border-subtle bg-surface px-3 py-3 transition-colors hover:border-border-strong">
                        <span
                          aria-hidden
                          className={cn(
                            "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-elevated",
                            color,
                          )}
                        >
                          <Icon size={16} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-ink">
                            {exec.workout_title || "Treino"}
                          </p>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted">
                            <span>{formatDate(exec.executed_at)}</span>
                            <span className="text-faint">·</span>
                            <span>{formatTime(exec.executed_at)}</span>
                            {duration ? (
                              <>
                                <span className="text-faint">·</span>
                                <span className="inline-flex items-center gap-0.5">
                                  <Clock size={9} aria-hidden />
                                  {duration}
                                </span>
                              </>
                            ) : null}
                            {exec.items.length > 0 ? (
                              <>
                                <span className="text-faint">·</span>
                                <span>
                                  {completedItems}/{exec.items.length} ex.
                                </span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
